import { useState } from 'react'
import { Icon, IconType, OrderClipModal, Slider } from '../../../components'
import { CreatorInterests } from '../../../components/CreatorInterests'
import { Divider } from '../../../components/Divider'
import { VideoPlayer } from '../../../components/VideoPlayer'
import { Avatar } from '../../../elements'
import { Collaboration, CollaborationStatus, Creator } from '../../../gql/types'
import { formatLongMessage } from '../../../utils/helpers'
import { AcceptApplicantButton } from '../AcceptApplicantButton'
import { PortfolioGridNew } from '../../../components/PortfolioGridNew'
import { childAgeRangeLocalizedString } from '../../../utils/GqlStrings'
import { petLocalizedString } from '../../../utils/GqlStrings'
import { mapPetToIcon, mapChildToIcon } from '../../../utils/mappers'

interface IProps {
  open: boolean
  setOpen: any
  creator: Creator
  collaboration?: Collaboration
  onCollaborationAccepted?: () => void
}

const ViewCreatorSlider = ({
  open,
  setOpen,
  creator,
  collaboration,
  onCollaborationAccepted,
}: IProps) => {
  const [isOrderClipOpen, setIsOrderClipOpen] = useState(false)

  return (
    <>
      {collaboration && isOrderClipOpen && (
        <OrderClipModal
          campaign={collaboration.campaign}
          title={`You've used up your paid clips`}
          description={`To approve more creators and get more clips, select the number of additional videos you would like and hit 'Confirm your order' below.`}
          open={isOrderClipOpen}
          setOpen={setIsOrderClipOpen}
        />
      )}

      <Slider setOpen={setOpen} open={open} size='md'>
        <div className='flex h-full flex-col overflow-y-auto bg-white shadow-xl w-full'>
          {/* Header */}
          <div className='sticky top-0 z-10 bg-white'>
            <div className='px-4 pt-4 sm:px-6'>
              <div className='flex items-start justify-between'>
                <Avatar
                  type='user'
                  src={creator.avatar?.url}
                  size='md'
                  alt={creator.firstName}
                />
                <div className='ml-3 flex h-7 items-center'>
                  <button
                    type='button'
                    className='rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none'
                    onClick={() => setOpen(false)}
                  >
                    <span className='sr-only'>Close panel</span>
                    <Icon
                      type={IconType.x}
                      className='h-6 w-6'
                      aria-hidden='true'
                    />
                  </button>
                </div>
              </div>
              <h3 className='text-xl font-bold text-gray-900 sm:text-2xl mt-4'>
                {creator?.firstName}
              </h3>
              {collaboration &&
                collaboration.status === CollaborationStatus.creatorApplied && (
                  <AcceptApplicantButton
                    collaboration={collaboration}
                    onCollaborationAccepted={onCollaborationAccepted}
                  />
                )}
            </div>
            <Divider className='py-6' />
          </div>

          {/* Main */}
          <div className='pb-1 sm:pb-6 px-4 h-full flex flex-col sm:px-6 overflow-auto'>
            {creator.bio && (
              <div className='flex-1'>
                <h4 className='text-sm font-medium text-gray-700'>About</h4>
                <div className='mt-1.5 sm:items-end flex-wrap'>
                  {creator.bio}
                </div>
              </div>
            )}

            {creator.occupation && (
              <div className='flex-1 mt-6'>
                <h4 className='text-sm font-medium text-gray-700'>
                  Occupation
                </h4>
                <div className='mt-1.5 sm:items-end flex-wrap'>
                  {creator.occupation}
                </div>
              </div>
            )}

            {creator?.age && (
              <div className='flex-1 mt-6'>
                <h4 className='text-sm font-medium text-gray-700'>Age</h4>
                <div className='mt-1.5 sm:items-end flex-wrap'>
                  {creator.age} years
                </div>
              </div>
            )}

            {creator?.childAgeRanges && (
              <div className='flex-1 mt-6'>
                <h4 className='text-sm font-medium text-gray-700'>
                  Children's Ages
                </h4>
                <div className='mt-1.5 sm:items-end flex-wrap'>
                  {creator.childAgeRanges.map((childAgeRange, index) => (
                    <span key={index} className='inline-flex items-center mr-2'>
                      {childAgeRangeLocalizedString(childAgeRange)}
                      {mapChildToIcon(childAgeRange) && (
                        <Icon
                          type={mapChildToIcon(childAgeRange)}
                          className='ml-1 w-4 h-4'
                          aria-hidden='true'
                        />
                      )}
                      {index < creator.childAgeRanges.length - 1 && ', '}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {creator?.petsOwner && (
              <div className='flex-1 mt-6'>
                <h4 className='text-sm font-medium text-gray-700'>Pets</h4>
                <div className='mt-1.5 sm:items-end flex-wrap'>
                  {creator.petsOwner.map((petOwner, index) => (
                    <span key={index} className='inline-flex items-center mr-2'>
                      {petLocalizedString(petOwner)}
                      {mapPetToIcon(petOwner) && (
                        <Icon
                          type={mapPetToIcon(petOwner)}
                          className='ml-1 w-4 h-4'
                          aria-hidden='true'
                        />
                      )}
                      {index < creator.petsOwner.length - 1 && ', '}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {collaboration && collaboration.creatorApplicationMessage && (
              <div className='flex-1 mt-6'>
                <h4 className='text-sm font-medium text-gray-700'>
                  Application
                </h4>
                <div
                  className='mt-1.5 sm:items-end flex-wrap'
                  dangerouslySetInnerHTML={{
                    __html: formatLongMessage(
                      collaboration.creatorApplicationMessage,
                    ),
                  }}
                ></div>
              </div>
            )}

            <div className='flex-1 mt-6'>
              <h4 className='text-sm font-medium text-gray-700'>Interests</h4>
              <div className='mt-1.5 sm:items-end flex-wrap'>
                {creator.interests?.map((interest, index) => (
                  <CreatorInterests key={index} interest={interest} />
                ))}
              </div>
            </div>

            {creator.introVideo?.url && (
              <div className='sm:flex-1 mt-6'>
                <h4 className='text-sm font-medium text-gray-700'>
                  Introduction Video
                </h4>
                <div className='mt-6 w-full max-w-sm mx-auto'>
                  <div className='relative isolate flex flex-col justify-end shadow-sm shadow-black/20 dark:shadow-white/20 overflow-hidden rounded-2xl bg-gray-900 aspect-[0.7]'>
                    <VideoPlayer
                      src={creator.introVideo?.url}
                      poster={creator.thumbnail?.url}
                      className='w-full h-full rounded-lg shadow-lg object-cover focus:outline-none'
                      controls
                    />
                  </div>
                </div>
              </div>
            )}

            <h4 className='mt-6 text-sm font-medium text-gray-700'>
              Portfolio
            </h4>
            <PortfolioGridNew creator={creator} useWindow={false} />
          </div>
        </div>
      </Slider>
    </>
  )
}

export { ViewCreatorSlider }
